// ─────────────────────────────────────────────────────────────────────────────
// authClaims (client) — getting a FRESH token after the company claim is minted
//
// `storage.rules` reads the caller's company from `request.auth.token.companyId`
// (see functions/authClaims.js for why it stopped reading Firestore). A custom
// claim only appears in a token that was minted AFTER the claim was set, and
// Firebase caches an ID token for about an hour. So there are exactly two
// moments that need handling, and this file is both of them:
//
//   1. RIGHT AFTER a company is assigned — signup and onboarding. The claim is
//      minted by a Firestore trigger a second or two after the user document is
//      written, so the client has to WAIT for it, not merely ask once.
//      -> waitForCompanyClaim()
//
//   2. ON LOAD, for a session that predates the claim — anyone signed in when
//      this shipped, and anyone whose company changed in another tab or from
//      the server while they were signed in.
//      -> reconcileCompanyClaim(), called from AuthContext
//
// WHY POLL RATHER THAN REFRESH ONCE. `getIdToken(true)` forces a round trip and
// returns whatever the Auth service currently holds. If the trigger has not
// finished, that is a fresh token WITHOUT the claim, and a single refresh would
// report failure on a system that was working correctly and about to succeed.
// Polling turns a race into a wait.
//
// WHY THERE IS A CEILING ON IT. If the claim genuinely cannot be minted — the
// trigger is not deployed, or it is failing — an unbounded loop would hammer
// the token endpoint forever and present as a hang. Every entry point here
// gives up after a bounded time and says so in the console, and the caller
// continues. The user then meets a Storage permission error rather than a
// frozen screen: failing closed, visibly, which is the behaviour the audit
// asked for.
// ─────────────────────────────────────────────────────────────────────────────

// Must match COMPANY_CLAIM in functions/authClaims.js and the field
// storage.rules reads.
export const COMPANY_CLAIM = 'companyId';

/**
 * The companyId currently carried by this user's ID token, or null.
 *
 * `forceRefresh` false reads the CACHED token — which is the honest answer to
 * "what would a Storage request send right now", and is what the reconcile path
 * needs before deciding whether a refresh is warranted.
 */
export async function readCompanyClaim(user, { forceRefresh = false } = {}) {
  if (!user) return null;
  try {
    const result = await user.getIdTokenResult(forceRefresh);
    const value = result && result.claims ? result.claims[COMPANY_CLAIM] : null;
    return typeof value === 'string' && value ? value : null;
  } catch (err) {
    console.warn('[authClaims] could not read ID token claims:', err?.code || err?.message || err);
    return null;
  }
}

/**
 * Block until this user's token carries `expectedCompanyId`, or the timeout
 * passes. Returns true if the claim arrived.
 *
 * Call this immediately after a company is assigned and BEFORE navigating to
 * anything that touches Storage. It never throws: a signup must not die because
 * a claim was slow, and the caller has no better recovery than continuing.
 */
export async function waitForCompanyClaim(user, expectedCompanyId, {
  timeoutMs = 12000,
  intervalMs = 700,
} = {}) {
  if (!user || !expectedCompanyId) return false;
  const deadline = Date.now() + timeoutMs;
  let attempt = 0;

  while (Date.now() < deadline) {
    attempt += 1;
    // force: true on every attempt — a cached token is exactly what we are
    // trying to get past.
    const claim = await readCompanyClaim(user, { forceRefresh: true });
    if (claim === expectedCompanyId) {
      if (attempt > 1) console.info(`[authClaims] company claim arrived after ${attempt} attempts.`);
      return true;
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  console.warn(
    `[authClaims] company claim did not arrive within ${timeoutMs}ms (expected ${expectedCompanyId}). ` +
    'File uploads and downloads will be refused until it does. If this persists, the syncCompanyClaim ' +
    'Firestore trigger is not deployed or is failing — see functions/authClaims.js.'
  );
  return false;
}

/**
 * Make an EXISTING session's token agree with the user document, refreshing it
 * only when it actually disagrees.
 *
 * This is the migration path for every session that was open when the claim
 * shipped, and it doubles as the live-update path for a company change made
 * from the server. `profileCompanyId` is the authority (the trigger derives the
 * claim from that same field), so the comparison is one-directional: if the
 * token disagrees, the token is stale.
 *
 * Returns true when the token ends up carrying the expected value.
 */
export async function reconcileCompanyClaim(user, profileCompanyId) {
  if (!user) return false;

  const cached = await readCompanyClaim(user);
  const expected = typeof profileCompanyId === 'string' && profileCompanyId ? profileCompanyId : null;

  // Already agrees — including the both-null case, which is a signed-in user
  // who has not created a company yet. No refresh, no network.
  if (cached === expected) return true;

  // No company on the profile but a claim on the token: the user was removed
  // from a company, or their document was rewritten. One forced refresh settles
  // it; there is nothing to wait FOR, so this does not poll.
  if (expected === null) {
    const after = await readCompanyClaim(user, { forceRefresh: true });
    return after === null;
  }

  console.info('[authClaims] token is missing or has a stale company claim — refreshing.');
  return waitForCompanyClaim(user, expected, { timeoutMs: 8000, intervalMs: 600 });
}

// ─────────────────────────────────────────────────────────────────────────────
// The last-resort recovery, at the point of use.
//
// AuthContext reconciles on load and whenever the profile's company changes, so
// by the time a user reaches an upload button the token is almost always right.
// "Almost always" leaves one window worth closing: a reconcile still IN FLIGHT
// when the user acts, on the first seconds of a page load. Without this, that
// window presents as "Upload failed — try again", and trying again is in fact
// the correct advice, which is exactly why it is such a bad error message — it
// gives the user no way to tell a transient token from a real permission
// problem.
//
// So a permission denial gets ONE reconcile and ONE retry. If the claim was the
// problem the retry succeeds and the user never learns there was one; if it was
// not, the original error is re-thrown unchanged and the caller's normal error
// handling runs. Retrying is safe for the upload paths this wraps: a denied
// Storage write leaves nothing behind, so the operation starts from the same
// state it started from the first time.
// ─────────────────────────────────────────────────────────────────────────────
const PERMISSION_DENIED = /storage\/unauthorized|permission-denied|storage\/unauthenticated/;

export async function withCompanyClaim(user, companyId, operation) {
  try {
    return await operation();
  } catch (err) {
    if (!user || !companyId || !PERMISSION_DENIED.test(err?.code || '')) throw err;
    console.info('[authClaims] a storage operation was denied — checking the company claim before giving up.');
    const ok = await reconcileCompanyClaim(user, companyId);
    if (!ok) throw err;
    return operation();
  }
}

/**
 * Why did a storage operation get refused — a stale token, or a real permission
 * problem? Returns a sentence to show the user, or null when the claim is fine
 * and the caller's own message is the honest one.
 *
 * Deliberately NOT a blanket "your session expired": saying that when the claim
 * is present and correct would send someone to re-authenticate over a problem
 * signing in again cannot fix.
 */
export async function explainStorageDenial(err, user, companyId) {
  if (!PERMISSION_DENIED.test(err?.code || '')) return null;
  const claim = await readCompanyClaim(user);
  if (claim && claim === companyId) return null;
  return 'Your session is still finishing setup. Reload the page and try again — '
       + 'if it keeps happening, sign out and back in.';
}
